import { Phone, MessageCircle, MapPin, Mail, Navigation } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import ContactForm from "./ContactForm";

export default function Contact() {
  const { t, pick } = useI18n();
  const { site } = useSite();
  if (!site) return null;
  const wa = site.whatsapp_number?.replace(/\D/g, "");
  const mapsHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(site.address)}`;

  return (
    <section id="contact" data-testid="contact" className="relative bg-zinc-950 text-white py-20 sm:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(230,57,70,0.12),transparent_60%)] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="tech-label !text-white/50 mb-4">{t("contact.label")}</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {t("contact.title")}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-lg">{t("contact.subtitle")}</p>

          <div className="mt-10 grid gap-4">
            <a
              data-testid="contact-call-primary"
              href={`tel:${site.phone_primary}`}
              className="flex items-center justify-between border border-white/15 hover:border-brand-red hover:bg-brand-red/10 p-5 group transition-all"
            >
              <div className="flex items-center gap-4">
                <Phone className="text-brand-red" size={22} />
                <div>
                  <div className="tech-label !text-white/40 !text-[10px]">{t("contact.callPrimary")}</div>
                  <div className="font-display font-extrabold text-2xl tracking-tight">{site.phone_primary}</div>
                </div>
              </div>
              <span className="text-white/40 group-hover:text-brand-red transition-colors text-xl">→</span>
            </a>
            {site.phone_secondary && (
              <a
                data-testid="contact-call-secondary"
                href={`tel:${site.phone_secondary}`}
                className="flex items-center justify-between border border-white/15 hover:border-brand-red hover:bg-brand-red/10 p-5 group transition-all"
              >
                <div className="flex items-center gap-4">
                  <Phone className="text-brand-red" size={22} />
                  <div>
                    <div className="tech-label !text-white/40 !text-[10px]">{t("contact.callSecondary")}</div>
                    <div className="font-display font-extrabold text-2xl tracking-tight">{site.phone_secondary}</div>
                  </div>
                </div>
                <span className="text-white/40 group-hover:text-brand-red transition-colors text-xl">→</span>
              </a>
            )}
            {wa && (
              <a
                data-testid="contact-whatsapp"
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between border border-white/15 hover:border-brand-amber hover:bg-brand-amber/10 p-5 group transition-all"
              >
                <div className="flex items-center gap-4">
                  <MessageCircle className="text-brand-amber" size={22} />
                  <div>
                    <div className="tech-label !text-white/40 !text-[10px]">{t("contact.whatsapp")}</div>
                    <div className="font-display font-extrabold text-xl tracking-tight">+{wa}</div>
                  </div>
                </div>
                <span className="text-white/40 group-hover:text-brand-amber transition-colors text-xl">→</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Form + Map row */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 lg:mt-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <ContactForm />
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="border border-white/15 bg-zinc-900/40">
            <div className="aspect-[4/3] w-full overflow-hidden">
              <iframe
                title="GBS Collision Center location"
                src={site.google_maps_embed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="p-5 sm:p-6 space-y-4 border-t border-white/15">
              <div>
                <div className="tech-label !text-white/40 mb-2 flex items-center gap-2"><MapPin size={12} /> {t("contact.addressLabel")}</div>
                <div className="font-display font-bold text-base tracking-tight">{site.address}</div>
              </div>
              <div>
                <div className="tech-label !text-white/40 mb-2">{t("contact.hoursLabel")}</div>
                <div className="text-sm text-zinc-300 leading-relaxed">{pick(site.hours)}</div>
              </div>
              {site.email && (
                <div>
                  <div className="tech-label !text-white/40 mb-2 flex items-center gap-2"><Mail size={12} /> Email</div>
                  <a href={`mailto:${site.email}`} className="text-sm text-zinc-200 hover:text-brand-amber break-all">{site.email}</a>
                </div>
              )}
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redHover text-white px-4 py-3 font-bold text-sm justify-center"
                data-testid="contact-directions"
              >
                <Navigation size={16} /> {t("contact.directions")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
